import { useState } from "react";
import { Search } from "lucide-react";
import { Button, Card, CardContent, Input } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { ErroConsulta } from "@/components/ErroConsulta";
import { UserRow, type AdminUserRow } from "@/components/UserRow";

const PAGE_SIZE = 20;

export function AdminUsers() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const { data, isLoading, error } = trpc.admin.users.list.useQuery({
    search: search.trim() || undefined,
    take: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Usuarios</h1>
        <p className="mt-1 text-sm text-admin-700">
          Todos os cadastros da plataforma.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-admin-700/50" />
          <Input
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="border-admin-200 bg-white pl-9"
          />
        </div>
        <div className="text-sm text-admin-700">
          {isLoading ? "..." : `${total} resultado${total === 1 ? "" : "s"}`}
        </div>
      </div>

      <Card className="border-admin-200">
        <CardContent className="p-0">
          {error ? (
            <div className="p-5">
              <ErroConsulta error={error} />
            </div>
          ) : isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-900 border-t-transparent" />
            </div>
          ) : data?.users.length === 0 ? (
            <div className="py-16 text-center text-sm text-admin-700">
              Nenhum usuario encontrado.
            </div>
          ) : (
            <div>
              {data?.users.map((user: AdminUserRow) => (
                <UserRow key={user.id} user={user} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-admin-700">
            Pagina {page + 1} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
              disabled={page + 1 >= totalPages}
            >
              Proxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
